import SelectBotModal from '../../../components/SelectBotModal';
import { loadWhatsAppSDK, parseWhatsAppSession, waitForWhatsAppSession } from '../../../utils/whatsappSignup';
import { startWixConnect } from '../../../utils/wixConnect';
/**
 * StepChannels — step 6 of the agent-creation flow: put the agent where the
 * visitors are.
 *
 * Three blocks, in the order that matters to the customer:
 *   1. Your website — pick the platform, connect right here (Wix / Webflow
 *      natively, WordPress plugin, Shopify app, Squarespace and 30+ others via
 *      the copy-paste guide). Selecting a tile expands its panel in place.
 *   2. Chat link — the hosted page + a QR code, for emails, bios and print.
 *   3. Messaging apps — Facebook, Instagram, WhatsApp, Telegram and Slack, connected without leaving the flow (OAuth in a popup).
 * Everything is optional; Continue always works. Whatever the customer skips is
 * still available later under Deploy → Channels.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import WebflowPublishNotice from '../../../components/WebflowPublishNotice';
import { startWebflowConnect } from '../../../utils/webflowConnect';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextField, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faClone, faArrowUpRightFromSquare, faLink, faMagnifyingGlass, faArrowLeft,
  faQrcode, faDownload, faGlobe, faComment, faWindowMaximize, faPlug, faCircleInfo, faLayerGroup,
  faChevronDown, faEnvelope, faShareNodes, faPrint,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { CHANNEL_GUIDES } from '../../../data/channelGuides.js';
import ChannelGuide from '../../../components/ChannelGuide.js';
import ShopifyChannelStatus from '../../../components/ShopifyChannelStatus.js';
import TelegramConnectModal from '../../../components/TelegramConnectModal.js';
import SlackConnectModal from '../../../components/SlackConnectModal.js';
import WhatsAppSetupModal from '../../../components/WhatsAppSetupModal.js';
import { SlackBrandIcon } from '../../../components/BrandIcons';
import { canSeeWhatsApp } from '../../../utils/featureFlags.js';
import { guardWrite } from '../../../utils/permissions.js';
import { qrEncode, qrSvgPath } from '../qr.js';
import { API, FIELD_SX } from '../steps.js';
import StepFooter from './StepFooter.js';
import './StepChannels.css';

const CHAT_BASE = 'https://chat.ultimo-bots.com/';
const userId = () => localStorage.getItem('user_id') || '';

// ── website platforms ────────────────────────────────────────────────────
const guideByKey = (k) => CHANNEL_GUIDES.find((g) => g.key === k);
// Generic "any website" guides (same shape as CHANNEL_GUIDES; the cguide_* keys translate them).
const WIDGET_GUIDE = {
  key: 'website-widget', name: 'Website widget', embed: 'widget', preview: true,
  intro: 'Add the Ultimo Bots chat widget to any website. Copy the snippet, paste it once before the closing </body> tag, and a floating chat bubble appears on every page.',
  steps: [
    { title: 'Copy the widget code', body: 'Copy the snippet below. It loads a floating chat bubble in the corner of your site.', placeSnippet: true },
    { title: 'Paste it into your site', body: "Paste it right before the closing `</body>` tag, or into your site's custom code / footer area so it loads on every page." },
    { title: 'Save and publish', body: 'Save and publish your site, then reload it to confirm the chat bubble appears.' },
  ],
};
const IFRAME_GUIDE = {
  key: 'website-iframe', name: 'Website iframe', embed: 'iframe', preview: true,
  intro: 'Embed the Ultimo Bots chat directly inside a page as an inline element. Paste the iframe wherever you want the chat to appear.',
  steps: [
    { title: 'Copy the iframe code', body: 'Copy the iframe snippet below.', placeSnippet: true },
    { title: 'Paste it where you want the chat', body: 'Paste it into an HTML / embed block on the page where the chat should appear. Adjust the `height` value if you want a taller chat area.' },
    { title: 'Save and publish', body: 'Save and publish the page, then open it to see the inline chat.' },
  ],
};
const PLATFORMS = [
  { key: 'wix', name: 'Wix', logo: '/wix-logo.png', wide: true },
  { key: 'wordpress', name: 'WordPress', logo: '/wordpress-logo.png' },
  { key: 'shopify', name: 'Shopify', logo: '/shopify-logo.svg' },
  { key: 'webflow', name: 'Webflow', logo: '/webflow-logo.png' },
  { key: 'squarespace', name: 'Squarespace', logo: '/squarespace-logo.svg' },
  { key: 'other', name: null, icon: faLayerGroup },
];
// Everything selectable under "Something else": generic snippet, iframe, then
// every researched guide except the ones that already have a tile.
const OTHER_OPTIONS = [
  { key: 'website-widget', nameKey: 'ac_ch_any_site', subKey: 'ac_ch_any_site_sub', icon: faComment, guide: WIDGET_GUIDE },
  { key: 'website-iframe', nameKey: 'ac_ch_iframe', subKey: 'ac_ch_iframe_sub', icon: faWindowMaximize, guide: IFRAME_GUIDE },
  ...CHANNEL_GUIDES.filter((g) => !['shopify', 'squarespace'].includes(g.key)).map((g) => ({ key: g.key, name: g.name, sub: null, logo: g.logo, guide: g })),
];

// ── messaging platforms ──────────────────────────────────────────────────
const SOCIAL = [
  { key: 'facebook', name: 'Facebook', descKey: 'channels_facebook_desc', logo: '/facebook-logo.webp', oauth: true },
  { key: 'instagram', name: 'Instagram', descKey: 'channels_instagram_desc', logo: '/instagram-logo.webp', oauth: true },
  { key: 'telegram', name: 'Telegram', descKey: 'channels_telegram_desc', fa: faTelegram, color: '#229ED9' },
  { key: 'slack', name: 'Slack', descKey: 'channels_slack_desc', slack: true },
  { key: 'whatsapp', name: 'WhatsApp', descKey: 'channels_whatsapp_desc', logo: '/whatsapp-logo.webp', flag: true },
];

// ── popup helper (OAuth without leaving the flow) ────────────────────────
function openPopup(url, name) {
  const w = 620; const h = 740;
  const left = Math.max(0, window.screenX + (window.outerWidth - w) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - h) / 2);
  return window.open(url, name, `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`);
}
/** Polls a popup: `onHome` when it lands back on our origin (we close it), `onClosed` when it is gone. */
function watchPopup(popup, { onHome, onClosed }) {
  const id = setInterval(() => {
    if (!popup || popup.closed) { clearInterval(id); onClosed?.(); return; }
    if (!onHome) return;
    try {
      const loc = popup.location;
      if (loc && loc.origin === window.location.origin && loc.pathname && loc.pathname !== '/wix-connect') {
        clearInterval(id);
        try { popup.close(); } catch { /* ignore */ }
        onHome();
      }
    } catch { /* cross-origin while on the provider — keep polling */ }
  }, 700);
  return () => clearInterval(id);
}

const Spinner = () => <span className="ac-spinner ac-spinner-sm" aria-hidden="true" />;

// ═════════════════════════════════════════════════════════════════════════
export default function StepChannels({ agent, botId, goNext, goBack, notify, isWebflow, returnPath }) {
  const { t } = useTranslation();
  const location = useLocation();
  const memKey = `ac:ch:${botId}`;
  const [platform, setPlatform] = useState(() => {
    try { const m = sessionStorage.getItem(memKey); if (m) return m; } catch { /* ignore */ }
    if (isWebflow) return 'webflow';
    if (agent?.wix_instance_id || agent?.wix_product?.status === 'installed') return 'wix';
    return null;
  });
  const choose = (k) => {
    const next = platform === k ? null : k;
    setPlatform(next);
    try { next ? sessionStorage.setItem(memKey, next) : sessionStorage.removeItem(memKey); } catch { /* ignore */ }
  };
  const platforms = useMemo(() => {
    if (!isWebflow) return PLATFORMS;
    const wf = PLATFORMS.find((p) => p.key === 'webflow');
    return [wf, ...PLATFORMS.filter((p) => p.key !== 'webflow')];
  }, [isWebflow]);

  // ── website: live state (from Wix / Webflow status) for the section badge ──
  const [liveOn, setLiveOn] = useState(null); // string site label when live

  // ── messaging connections ──
  const [connections, setConnections] = useState([]);
  const refreshConnections = useCallback(async () => {
    try {
      const r = await fetch(`${API}messaging_connections?user_id=${encodeURIComponent(userId())}`);
      if (!r.ok) return;
      const d = await r.json();
      setConnections((Array.isArray(d) ? d : []).filter((c) => String(c.bot_id) === String(botId)));
    } catch { /* ignore */ }
  }, [botId]);
  useEffect(() => { refreshConnections(); }, [refreshConnections]);
  // Popup OAuth pages post oauth-success to their opener (the Channels page does) — refresh then, too.
  useEffect(() => {
    const onMsg = (e) => { if (e.origin === window.location.origin && e.data?.type === 'oauth-success') refreshConnections(); };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [refreshConnections]);

  const [replySettingsPlatform, setReplySettingsPlatform] = useState(null);
  const [waReplySettings, setWaReplySettings] = useState({ autoResponseMode: 'respond_all', pauseOnManualReply: true });
  const [connecting, setConnecting] = useState(null); // social key with an open popup
  const popupRef = useRef(null);
  const stopWatchRef = useRef(null);
  useEffect(() => () => { stopWatchRef.current?.(); }, []);

  const connectOAuth = async (key, autoResponseMode = 'respond_all', pauseOnManualReply = true) => {
    if (!guardWrite()) return;
    if (connecting) return;
    setConnecting(key);
    try {
      const r = await fetch(`${API}auth/${key}/connect?bot_id=${encodeURIComponent(botId)}&auto_response_mode=${encodeURIComponent(autoResponseMode)}&pause_on_manual_reply=${pauseOnManualReply}`);
      const d = await r.json().catch(() => ({}));
      if (!d.authorizeUrl) throw new Error('no url');
      const popup = openPopup(d.authorizeUrl, `${key}-oauth`);
      if (!popup) { window.location.href = d.authorizeUrl; return; } // popup blocked → same-window OAuth
      popupRef.current = popup;
      stopWatchRef.current = watchPopup(popup, {
        onHome: () => { setConnecting(null); refreshConnections().then(() => notify(t('ac_ch_connected_toast', { name: SOCIAL.find((s) => s.key === key)?.name || key }))); },
        onClosed: () => { setConnecting(null); refreshConnections(); },
      });
    } catch (e) {
      console.error(e); setConnecting(null); notify(t('ac_ch_connect_failed'), 'error');
    }
  };

  const [telegramOpen, setTelegramOpen] = useState(false);
  const [slackOpen, setSlackOpen] = useState(false);
  // WhatsApp — Meta's Embedded Signup (same flow as the Channels page): the
  // customer picks their situation in WhatsAppSetupModal, then FB.login opens
  // Meta's wizard; the session message + code go to auth/whatsapp/callback.
  const [waSetupOpen, setWaSetupOpen] = useState(false);
  const waSessionRef = useRef(null);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== 'https://www.facebook.com' && e.origin !== 'https://web.facebook.com') return;
      try {
        const d = parseWhatsAppSession(e);
        if (!d) return;
        if (d.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(d.event)) waSessionRef.current = { phone_number_id: d.data?.phone_number_id, waba_id: d.data?.waba_id };
        else if (d.event === 'CANCEL') { setConnecting(null); waSessionRef.current = null; }
        else if (d.event === 'ERROR') { setConnecting(null); waSessionRef.current = null; notify(t('social_whatsapp_signup_error', { error: d.data?.error_message || '' }), 'error'); }
      } catch { /* not JSON */ }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [notify, t]);
  const finishWhatsApp = (result) => {
    notify(t('social_whatsapp_connected', { phone: result.phone_number || 'N/A' }));
    const a = result.activation;
    if (a && (!a.subscribed || !a.registered)) notify(t('social_whatsapp_complete_error', { error: (a.errors || []).join('; ') || `status=${a.phone_status || 'unknown'}` }), 'error');
    refreshConnections();
  };
  const connectWhatsApp = async (featureType) => {
    setWaSetupOpen(false);
    setConnecting('whatsapp'); waSessionRef.current = null;
    try {
      const r = await fetch(`${API}auth/whatsapp/connect?bot_id=${encodeURIComponent(botId)}&feature_type=${encodeURIComponent(featureType)}&auto_response_mode=${encodeURIComponent(waReplySettings.autoResponseMode)}&pause_on_manual_reply=${waReplySettings.pauseOnManualReply}`);
      if (!r.ok) throw new Error('config');
      const config = await r.json();
      await loadWhatsAppSDK(config.appId);
      window.FB.login((fb) => {
        if (!fb.authResponse) { setConnecting(null); waSessionRef.current = null; return; }
        (async () => {
          try {
            const session = await waitForWhatsAppSession(waSessionRef);
            const cb = await fetch(`${API}auth/whatsapp/callback`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: fb.authResponse.code, state: config.state, waba_id: session?.waba_id || null, phone_number_id: session?.phone_number_id || null }) });
            const result = await cb.json().catch(() => ({}));
            if (!cb.ok || !result.success) throw new Error(result.detail || t('social_whatsapp_complete_failed'));
            finishWhatsApp(result);
          } catch (e) { console.error(e); notify(t('social_whatsapp_complete_error', { error: e.message }), 'error'); }
          finally { setConnecting(null); waSessionRef.current = null; }
        })();
      }, { config_id: config.configId, response_type: 'code', override_default_response_type: true,
        extras: { sessionInfoVersion: '3', version: 'v4', ...(featureType ? { featureType } : {}) } });
    } catch (e) { console.error(e); setConnecting(null); notify(t('ac_ch_connect_failed'), 'error'); }
  };
  const connectWhatsAppManual = async ({ phone_number_id, access_token, waba_id }) => {
    const r = await fetch(`${API}messaging_connections/whatsapp/manual`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bot_id: botId, phone_number_id, access_token, waba_id, auto_response_mode: waReplySettings.autoResponseMode, pause_on_manual_reply: waReplySettings.pauseOnManualReply }) });
    const result = await r.json().catch(() => ({}));
    if (!r.ok || !result.success) throw new Error(result.detail || t('social_whatsapp_complete_failed'));
    setWaSetupOpen(false);
    finishWhatsApp(result);
  };
  const socials = useMemo(() => SOCIAL.filter((s) => !s.flag || canSeeWhatsApp()), []);

  // ── chat link + QR ──
  const link = `${CHAT_BASE}${botId}`;
  const qr = useMemo(() => { try { return qrEncode(link, 'M'); } catch { return null; } }, [link]);
  const qrPath = useMemo(() => (qr ? qrSvgPath(qr) : ''), [qr]);
  const [copied, setCopied] = useState(false);
  const copyLink = () => {
    navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  };
  const downloadQr = () => {
    if (!qr) return;
    const s = qr.size + 8; // 4-module quiet zone
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${s} ${s}" width="1024" height="1024" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><path d="${qrPath}" fill="#000" transform="translate(4 4)"/></svg>`;
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    const a = document.createElement('a'); a.href = url; a.download = `${(agent?.name || 'agent').replace(/[^\w-]+/g, '_')}-chat-qr.svg`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Webflow OAuth round-trip lands here with { activated } in the router state.
  useEffect(() => {
    if (location.state?.webflow === true) {
      setPlatform('webflow');
      try { sessionStorage.setItem(memKey, 'webflow'); } catch { /* ignore */ }
      if (location.state?.activated) notify(t('integration_webflow_activated_success'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectedFor = (key) => connections.filter((c) => c.platform === key);
  const anyConnected = connections.length > 0 || Boolean(liveOn);

  // Collapsible sections: the website block starts open, the optional ones closed.
  const [open, setOpen] = useState({ web: true, msg: true, link: false });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  // Plain render helper (not a component) so the header DOM persists across renders and the chevron animates.
  const sectionHead = ({ id, n, title, extra }) => (
    <button type="button" className="ac-ch-section-head" onClick={(e) => { e.stopPropagation(); toggle(id); }} aria-expanded={open[id]} aria-controls={`ac-ch-${id}`}>
      <span className="ac-ch-num">{n}</span>
      <span className="ac-ch-section-title">{title}</span>
      {extra}
      <span className="ac-ch-chev"><FontAwesomeIcon icon={faChevronDown} /></span>
    </button>
  );

  return (
    <>
      <header className="ac-head">
        <h2 className="ac-title">{t('ac_ch_title', { name: agent?.name || '' })}</h2>
        <p className="ac-sub">{t('ac_ch_sub')}</p>
      </header>

      <div className="ac-body">
        {/* ── 1 · Your website ─────────────────────────────────────────── */}
        <section className={`ac-ch-section ${open.web ? 'is-open' : ''}`} onClick={open.web ? undefined : () => toggle('web')}>
          {sectionHead({ id: 'web', n: '1', title: t('ac_ch_web_title'),
            extra: liveOn ? <span className="ac-ch-live"><FontAwesomeIcon icon={faCircleCheck} />{t('ac_ch_live_on', { site: liveOn })}</span> : null })}
          <div className="ac-ch-body" id="ac-ch-web">
            <div className="ac-ch-body-inner">
              <div className="ac-ch-content">
                <div className="ac-ch-tiles" role="tablist" aria-label={t('ac_ch_web_title')}>
                  {platforms.map((p) => {
                    const active = platform === p.key;
                    return (
                      <button key={p.key} type="button" role="tab" aria-selected={active}
                        className={`ac-ch-tile ${active ? 'is-active' : ''} ${platform && !active ? 'is-dim' : ''}`}
                        onClick={() => choose(p.key)}>
                        <span className="ac-ch-tile-logo">
                          {p.logo ? <img src={p.logo} alt="" className={p.wide ? 'is-wide' : ''} /> : <FontAwesomeIcon icon={p.icon} />}
                        </span>
                        <span className="ac-ch-tile-name">{p.name || t('ac_ch_other')}</span>
                      </button>
                    );
                  })}
                </div>

                {platform && (
                  <div className="ac-ch-panel" key={platform}
                    // notch under the active tile: exact centre of grid column i (N equal columns, 10px gaps)
                    style={{ '--ac-ch-notch': `calc((100% - ${(platforms.length - 1) * 10}px) / ${platforms.length} * ${platforms.findIndex((p) => p.key === platform) + 0.5} + ${platforms.findIndex((p) => p.key === platform) * 10}px)` }}>
                    {platform === 'wix' && <WixPanel botId={botId} t={t} notify={notify} onLive={setLiveOn} />}
                    {platform === 'webflow' && <WebflowPanel botId={botId} t={t} notify={notify} onLive={setLiveOn} returnPath={returnPath} />}
                    {platform === 'wordpress' && <WordPressPanel botId={botId} t={t} />}
                    {platform === 'shopify' && <div className="ac-ch-guide"><ShopifyChannelStatus guide={guideByKey('shopify')} botId={botId} /></div>}
                    {platform === 'squarespace' && <div className="ac-ch-guide"><ChannelGuide guide={guideByKey('squarespace')} botId={botId} /></div>}
                    {platform === 'other' && <OtherPanel botId={botId} t={t} />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── 2 · Messaging apps ───────────────────────────────────────── */}
        <section className={`ac-ch-section ${open.msg ? 'is-open' : ''}`} onClick={open.msg ? undefined : () => toggle('msg')}>
          {sectionHead({ id: 'msg', n: '2', title: t('ac_ch_msg_title'),
            extra: connections.length ? <span className="ac-ch-live"><FontAwesomeIcon icon={faCircleCheck} />{t('ac_ch_connected_n', { n: connections.length })}</span> : null })}
          <div className="ac-ch-body" id="ac-ch-msg">
            <div className="ac-ch-body-inner">
              <div className="ac-ch-content">
                <div className="ac-ch-social">
                  {socials.map((s) => {
                    const conns = connectedFor(s.key);
                    const busy = connecting === s.key;
                    const onConnect = () => {
                      if (s.oauth) { if (guardWrite()) setReplySettingsPlatform(s.key); return; }
                      if (s.key === 'telegram') { if (guardWrite()) setTelegramOpen(true); return; }
                      if (s.key === 'slack') { if (guardWrite()) setSlackOpen(true); return; }
                      if (s.key === 'whatsapp') { if (guardWrite()) setReplySettingsPlatform('whatsapp'); }
                    };
                    return (
                      <div key={s.key} className={`ac-ch-card ${conns.length ? 'is-on' : ''}`}>
                        <span className="ac-ch-card-logo">
                          {s.logo && <img src={s.logo} alt="" />}
                          {s.fa && <FontAwesomeIcon icon={s.fa} style={{ color: s.color }} />}
                          {s.slack && <SlackBrandIcon size={30} />}
                        </span>
                        <span className="ac-ch-card-text">
                          <span className="ac-ch-card-name">{s.name}</span>
                          {conns.length > 0 && (
                            <span className="ac-ch-card-on" title={conns.map((c) => c.display_name).filter(Boolean).join(', ')}>
                              <FontAwesomeIcon icon={faCircleCheck} />
                              {conns.length === 1 && conns[0].display_name ? conns[0].display_name : t('ac_ch_connected_n', { n: conns.length })}
                            </span>
                          )}
                        </span>
                        {conns.length ? (
                          <button type="button" className="ac-ch-btn-o is-quiet" onClick={onConnect} disabled={busy}>{t('ac_ch_add_another')}</button>
                        ) : (
                          <button type="button" className="ac-ch-btn-o" onClick={onConnect} disabled={busy || (connecting && connecting !== s.key)}>
                            {busy ? <><Spinner />{t('ac_ch_waiting')}</> : <><FontAwesomeIcon icon={faPlug} />{t('connect')}</>}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3 · Chat link ────────────────────────────────────────────── */}
        <section className={`ac-ch-section ${open.link ? 'is-open' : ''}`} onClick={open.link ? undefined : () => toggle('link')}>
          {sectionHead({ id: 'link', n: '3', title: t('ac_ch_link_title') })}
          <div className="ac-ch-body" id="ac-ch-link">
            <div className="ac-ch-body-inner">
              <div className="ac-ch-content">
                <div className="ac-ch-link">
                  <div className="ac-ch-link-main">
                    <div className="ac-ch-link-top">
                      <span className="ac-ch-link-label">{t('ac_ch_link_label')}</span>
                      <a className="ac-link-btn ac-ch-link-open" href={link} target="_blank" rel="noopener noreferrer">{t('ac_ch_open')}<FontAwesomeIcon icon={faArrowUpRightFromSquare} /></a>
                    </div>
                    <div className={`ac-ch-link-field ${copied ? 'is-ok' : ''}`}>
                      <FontAwesomeIcon icon={faLink} className="ac-ch-link-icon" />
                      <span className="ac-ch-link-url" title={link}>{link}</span>
                      <button type="button" className={`ac-ch-copy ${copied ? 'is-ok' : ''}`} onClick={copyLink}>
                        <FontAwesomeIcon icon={copied ? faCircleCheck : faClone} />{copied ? t('cguide_copied') : t('integration_copy_link')}
                      </button>
                    </div>
                    <div className="ac-ch-uses">
                      {[
                        { icon: faEnvelope, k: 'ac_ch_use_email' },
                        { icon: faShareNodes, k: 'ac_ch_use_social' },
                        { icon: faPrint, k: 'ac_ch_use_print' },
                      ].map((u) => (
                        <div key={u.k} className="ac-ch-use">
                          <span className="ac-ch-use-icon"><FontAwesomeIcon icon={u.icon} /></span>
                          <span className="ac-ch-use-text"><strong>{t(u.k)}</strong><span>{t(`${u.k}_sub`)}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="ac-ch-qr">
                    {qr ? (
                      <svg className="ac-ch-qr-svg" viewBox={`0 0 ${qr.size} ${qr.size}`} shapeRendering="crispEdges" role="img" aria-label={t('ac_ch_qr_alt')}>
                        <path d={qrPath} fill="#20202a" />
                      </svg>
                    ) : <div className="ac-ch-qr-svg" />}
                    <button type="button" className="ac-ch-qr-dl" onClick={downloadQr}><FontAwesomeIcon icon={faDownload} />{t('ac_ch_qr_save')}</button>
                    <span className="ac-ch-qr-hint"><FontAwesomeIcon icon={faQrcode} />{t('ac_ch_qr_hint')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <StepFooter onBack={goBack} primaryLabel={t('ac_ch_cta')} onPrimary={goNext}
        hint={<><FontAwesomeIcon icon={faCircleInfo} />{anyConnected ? t('ac_ch_later') : t('ac_ch_later_none')}</>} />

      <TelegramConnectModal open={telegramOpen} onClose={() => setTelegramOpen(false)} defaultBotId={botId}
        onConnected={() => { refreshConnections(); notify(t('ac_ch_connected_toast', { name: 'Telegram' })); }} />
      <SlackConnectModal open={slackOpen} onClose={() => setSlackOpen(false)} defaultBotId={botId}
        onConnected={() => { refreshConnections(); notify(t('ac_ch_connected_toast', { name: 'Slack' })); }} />
      <SelectBotModal open={!!replySettingsPlatform} platform={replySettingsPlatform} defaultBotId={botId}
        onClose={() => setReplySettingsPlatform(null)}
        onConfirm={async (_, autoResponseMode, pauseOnManualReply) => {
          const target = replySettingsPlatform;
          setReplySettingsPlatform(null);
          if (target === 'whatsapp') {
            setWaReplySettings({ autoResponseMode, pauseOnManualReply });
            setWaSetupOpen(true);
          } else await connectOAuth(target, autoResponseMode, pauseOnManualReply);
        }} />
      <WhatsAppSetupModal open={waSetupOpen} onClose={() => setWaSetupOpen(false)} onSelect={connectWhatsApp} onManualConnect={connectWhatsAppManual} />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wix — official app, connected in a popup (the /wix-connect page closes itself).
function WixPanel({ botId, t, notify, onLive }) {
  const [status, setStatus] = useState(null); // {status, url, site_name}
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const stopRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}wix/status?bot_id=${encodeURIComponent(botId)}`);
      if (!r.ok) throw new Error('Could not check the Wix connection. Please retry.');
      const d = await r.json().catch(() => ({}));
      setStatus(d && d.status ? d : { status: 'no_token' });
      onLive(d?.status === 'token_with_code' ? (d.site_name || d.url || 'Wix') : null);
    } catch { setStatus({ status: 'error' }); }
  }, [botId, onLive]);
  useEffect(() => { load(); return () => { stopRef.current?.(); onLive(null); }; }, [load, onLive]);

  const connect = async () => {
    if (!guardWrite()) return;
    const result = await startWixConnect(botId, {popup: true, onConnected: load});
    const popup = result?.popup;
    if (!popup) return;
    setWaiting(true);
    stopRef.current = watchPopup(popup, { onClosed: async () => { setWaiting(false); await load(); } });
  };
  const activate = async () => {
    if (!guardWrite()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}wix/activate_chatbot?bot_id=${encodeURIComponent(botId)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (r.ok) { await load(); notify(t('ac_ch_wix_live_toast')); }
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="ac-ch-loading"><Spinner /></div>;
  if (status.status === 'error') return <div role="alert" className="ac-ch-native">
    <p>Could not check your Wix connection.</p><button className="ac-link-btn" onClick={load}>Retry</button>
  </div>;
  const site = status.site_name || status.url;
  const siteEl = status.url ? <a href={status.url} target="_blank" rel="noopener noreferrer">{site}</a> : <strong>{site || t('integration_unknown_site')}</strong>;

  if (status.status === 'token_with_code') {
    return (
      <div className="ac-ch-native">
        <div className="ac-ch-state is-live">
          <FontAwesomeIcon icon={faCircleCheck} />
          <div><strong>{t('ac_ch_state_live')}</strong><span>{t('ac_ch_state_live_sub')} {siteEl}</span></div>
        </div>
      </div>
    );
  }
  if (status.status === 'token_with_code_disabled') {
    return (
      <div className="ac-ch-native">
        <div className="ac-ch-state">
          <FontAwesomeIcon icon={faPlug} />
          <div><strong>{t('ac_ch_state_connected')}</strong><span>{t('ac_ch_state_connected_sub')} {siteEl}</span></div>
          <button type="button" className="ac-btn-primary ac-ch-btn" onClick={activate} disabled={busy}>{busy ? <Spinner /> : null}{t('ac_ch_make_live')}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="ac-ch-native">
      <div className="ac-ch-native-text">
        <img src="/wix-logo.png" alt="Wix" className="ac-ch-native-logo is-wide" />
        <div>
          <strong>{t('ac_ch_wix_head')}</strong>
          <p>{t('ac_ch_wix_text')}</p>
          <ul className="ac-ch-perks"><li>{t('ac_ch_wix_perk1')}</li><li>{t('ac_ch_wix_perk2')}</li><li>{t('ac_ch_wix_perk3')}</li></ul>
        </div>
      </div>
      <div className="ac-ch-native-actions">
        <button type="button" className="ac-btn-primary ac-ch-btn" onClick={connect} disabled={waiting}>
          {waiting ? <><Spinner />{t('ac_ch_waiting_wix')}</> : <><FontAwesomeIcon icon={faPlug} />{t('ac_ch_connect_wix')}</>}
        </button>
        {waiting && <button type="button" className="ac-link-btn" onClick={() => { stopRef.current?.(); setWaiting(false); load(); }}>{t('cancel')}</button>}
      </div>
    </div>
  );
}

// Webflow — OAuth round-trip (comes back to this step), then pick the site.
function WebflowPanel({ botId, t, notify, onLive, returnPath }) {
  const [status, setStatus] = useState(null); // {status, sites}
  const [siteId, setSiteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}webflow/status?bot_id=${encodeURIComponent(botId)}`);
      const d = await r.json().catch(() => ({}));
      const st = d && d.status ? d : { status: 'no_token' };
      setStatus(st);
      if (st.sites?.length) setSiteId((v) => v || st.sites[0].id);
      onLive(st.status === 'token_with_code' ? (st.sites?.[0]?.name || 'Webflow') : null);
    } catch { setStatus({ status: 'no_token' }); }
  }, [botId, onLive]);
  useEffect(() => { load(); return () => onLive(null); }, [load, onLive]);

  const connect = async () => {
    if (!guardWrite()) return;
    try {
      // The OAuth state is minted server-side and bound to this session.
      const res = await startWebflowConnect({ botId, returnTo: returnPath });
      if (res?.reconnected) await load();
    } catch (e) {
      console.error(e);
      setErr(t('ac_ch_connect_failed'));
    }
  };
  const activate = async () => {
    if (!siteId || !guardWrite()) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}webflow/activate_chatbot?bot_id=${encodeURIComponent(botId)}&site_id=${encodeURIComponent(siteId)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (r.status === 429) { const d = await r.json().catch(() => ({})); setErr(d.message || t('integration_webflow_rate_limit')); }
      else if (r.ok) { await load(); notify(t('ac_ch_webflow_live_toast')); }
      else setErr(t('ac_ch_connect_failed'));
    } catch (e) { console.error(e); setErr(t('ac_ch_connect_failed')); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="ac-ch-loading"><Spinner /></div>;
  if (status.status === 'token_with_code') {
    const site = status.sites?.[0];
    // On the site but not in the published page yet: say so, with the publish line.
    const pending = status.state ? status.state === 'pending' : !!status.needs_publish;
    return (
      <div className="ac-ch-native">
        <div className={`ac-ch-state${pending ? '' : ' is-live'}`}>
          <FontAwesomeIcon icon={faCircleCheck} />
          <div><strong>{pending ? t('wf_state_pending') : t('ac_ch_state_live')}</strong><span>{t('ac_ch_state_live_sub')} <strong>{site?.name || 'Webflow'}</strong></span></div>
        </div>
        {pending && site && (
          <WebflowPublishNotice inline botId={botId} siteId={site.id} siteName={site.name}
            text={t('wf_state_pending_text')} onPublished={() => setTimeout(load, 1500)} />
        )}
      </div>
    );
  }
  if (status.status === 'token_no_code') {
    return (
      <div className="ac-ch-native">
        <div className="ac-ch-native-text">
          <img src="/webflow-logo.png" alt="Webflow" className="ac-ch-native-logo" />
          <div><strong>{t('ac_ch_webflow_pick_head')}</strong><p>{t('ac_ch_webflow_pick_text')}</p></div>
        </div>
        <div className="ac-ch-webflow-pick">
          <TextField select size="small" label={t('ac_ch_webflow_site')} value={siteId} onChange={(e) => setSiteId(e.target.value)} sx={{ ...FIELD_SX, minWidth: 260 }}>
            {(status.sites || []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <button type="button" className="ac-btn-primary ac-ch-btn" onClick={activate} disabled={busy || !siteId}>{busy ? <Spinner /> : <FontAwesomeIcon icon={faPlug} />}{t('ac_ch_webflow_add')}</button>
        </div>
        {err && <div className="ac-error">{err}</div>}
      </div>
    );
  }
  return (
    <div className="ac-ch-native">
      <div className="ac-ch-native-text">
        <img src="/webflow-logo.png" alt="Webflow" className="ac-ch-native-logo" />
        <div>
          <strong>{t('ac_ch_webflow_head')}</strong>
          <p>{t('ac_ch_webflow_text')}</p>
          <ul className="ac-ch-perks"><li>{t('integration_webflow_bullet1')}</li><li>{t('integration_webflow_bullet2')}</li><li>{t('integration_webflow_bullet3')}</li></ul>
        </div>
      </div>
      <div className="ac-ch-native-actions">
        <button type="button" className="ac-btn-primary ac-ch-btn" onClick={connect}><FontAwesomeIcon icon={faPlug} />{t('ac_ch_connect_webflow')}</button>
        <span className="ac-ch-native-note">{t('ac_ch_webflow_note')}</span>
      </div>
    </div>
  );
}

// WordPress — official plugin: install, paste the agent ID.
function WordPressPanel({ botId, t }) {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard?.writeText(botId).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  const pluginUrl = process.env.REACT_APP_WP_PLUGIN_URL || 'https://wordpress.org/plugins/ultimo-bots/';
  return (
    <div className="ac-ch-native">
      <div className="ac-ch-native-text">
        <img src="/wordpress-logo.png" alt="WordPress" className="ac-ch-native-logo" />
        <div><strong>{t('ac_ch_wp_head')}</strong><p>{t('ac_ch_wp_text')}</p></div>
      </div>
      <ol className="ac-ch-steps">
        <li>
          <span className="ac-ch-step-n">1</span>
          <div className="ac-ch-step-body">
            <strong>{t('ac_ch_wp_s1')}</strong>
            <a className="ac-ch-btn-o" href={pluginUrl} target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faArrowUpRightFromSquare} />{t('integration_install_plugin')}</a>
          </div>
        </li>
        <li>
          <span className="ac-ch-step-n">2</span>
          <div className="ac-ch-step-body">
            <strong>{t('ac_ch_wp_s2')}</strong>
            <div className="ac-ch-id-row">
              <code className="ac-ch-id">{botId}</code>
              <button type="button" className={`ac-ch-btn-o ${copied ? 'is-ok' : ''}`} onClick={copy}><FontAwesomeIcon icon={copied ? faCircleCheck : faClone} />{copied ? t('cguide_copied') : t('ac_ch_copy_id')}</button>
            </div>
          </div>
        </li>
        <li>
          <span className="ac-ch-step-n">3</span>
          <div className="ac-ch-step-body"><strong>{t('ac_ch_wp_s3')}</strong><span className="ac-ch-step-sub">{t('ac_ch_wp_s3_sub')}</span></div>
        </li>
      </ol>
    </div>
  );
}

// "Something else" — search all researched guides + the generic snippet / iframe.
function OtherPanel({ botId, t }) {
  const [q, setQ] = useState('');
  const [pick, setPick] = useState(null);
  const options = useMemo(() => OTHER_OPTIONS.map((o) => ({ ...o, name: o.nameKey ? t(o.nameKey) : o.name, sub: o.subKey ? t(o.subKey) : o.sub })), [t]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.name.toLowerCase().includes(s) || (o.sub || '').toLowerCase().includes(s)) : options;
  }, [q, options]);
  if (pick) {
    return (
      <div className="ac-ch-guide">
        <div className="ac-ch-guide-bar">
          <button type="button" className="ac-link-btn" onClick={() => setPick(null)}><FontAwesomeIcon icon={faArrowLeft} />{t('ac_ch_all_platforms')}</button>
          <span className="ac-ch-guide-name">
            {pick.logo ? <img src={pick.logo} alt="" /> : <FontAwesomeIcon icon={pick.icon} />}
            {pick.name}
          </span>
        </div>
        <ChannelGuide guide={pick.guide} botId={botId} />
      </div>
    );
  }
  return (
    <div className="ac-ch-other">
      <div className="ac-ch-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ac_ch_search_ph')} autoFocus />
      </div>
      <div className="ac-ch-other-grid">
        {list.map((o) => (
          <button key={o.key} type="button" className="ac-ch-opt" onClick={() => setPick(o)}>
            <span className="ac-ch-opt-logo">{o.logo ? <img src={o.logo} alt="" /> : <FontAwesomeIcon icon={o.icon} />}</span>
            <span className="ac-ch-opt-text"><span className="ac-ch-opt-name">{o.name}</span>{o.sub && <span className="ac-ch-opt-sub">{o.sub}</span>}</span>
          </button>
        ))}
        {!list.length && <div className="ac-ch-other-empty"><FontAwesomeIcon icon={faGlobe} />{t('ac_ch_no_match')}</div>}
      </div>
    </div>
  );
}
